import { decodeJWT } from "@/utils/jwt";
import { SET_CACHE_AUTH, SET_ACTIVE_CACHE_ID } from "@/store/mutation-types";

import Vue from "vue";
import Router from "vue-router";
import store from "@/store";

// Login
import LoginView from "@/views/analytics/LoginView.vue";

// Cache
import CacheView from "@/views/cache/index.vue";
import OverviewView from "@/views/cache/OverviewView.vue";
import MetricsView from "@/views/cache/MetricsView.vue";
import MetricView from "@/views/cache/MetricView.vue";
import AlertsView from "@/views/cache/AlertsView.vue";
import SettingsView from "@/views/analytics/cache/SettingsView.vue";
import ConsultingView from "@/views/cache/ConsultingView.vue";

// Cache > Introspection
import IntrospectionView from "@/views/cache/introspection/index.vue";
import PopularKeysView from "@/views/cache/introspection/PopularKeysView.vue";

// Cache > Introspection > Keys
import KeysView from "@/views/cache/introspection/keys/index.vue";

import PrefixesView from "@/views/cache/introspection/PrefixesView.vue";

// Cache > Introspection > Prefixes > Prefix
import PrefixView from "@/views/cache/introspection/prefix/index.vue";
import PrefixOverviewView from "@/views/cache/introspection/prefix/OverviewView.vue";

// Cache > Introspection > Recent Requests
import RecentRequestsView from "@/views/cache/introspection/RecentRequestsView.vue";

// Style guide
import StyleGuideView from "@/views/StyleGuideView.vue";

// Playground (admin only routes)
import CompoundStatsView from "@/views/cache/playground/CompoundStatsView.vue";
import SlabStatsView from "@/views/cache/playground/SlabStatsView.vue";

Vue.use(Router);

// All routes require auth by default
const router = new Router({
  base: process.env.BASE_URL,
  routes: [
    {
      // Redirect
      path: "/",
      name: "home",
      beforeEnter: (to, from, next) => {
        const decodedJWT = decodeJWT(localStorage.getItem("token"));

        if (decodedJWT && decodedJWT.cache_id) {
          next({
            name: "overview",
            params: { cacheId: decodedJWT.cache_id.toString() },
          });
          return;
        }

        next({ name: "login" });
        return;
      },
    },
    {
      path: "/login",
      name: "login",
      component: LoginView,
      meta: { requiresAuth: false },
    },
    {
      path: "/sso/:token",
      name: "sso",
      meta: { requiresAuth: false },
      beforeEnter: (to, from, next) => {
        const token = to.params.token;

        const decodedJWT = decodeJWT(token);

        if (decodedJWT && decodedJWT.cache_id) {
          store.commit(SET_CACHE_AUTH, token);
          store.commit(SET_ACTIVE_CACHE_ID, decodedJWT.cache_id.toString());

          next({
            name: "overview",
            params: { cacheId: decodedJWT.cache_id.toString() },
          });
          return;
        }
        // falsy or malformed token
        next({ name: "login" });
        return;
      },
    },
    {
      path: "/caches/:cacheId",
      component: CacheView,
      children: [
        {
          path: "",
          name: "overview",
          component: OverviewView,
        },
        {
          path: "metrics",
          name: "metrics",
          component: MetricsView,
        },
        {
          path: "metrics/:metricId",
          name: "metric",
          component: MetricView,
        },
        {
          path: "settings",
          name: "settings",
          component: SettingsView,
        },
        {
          path: "consulting",
          name: "consulting",
          component: ConsultingView,
        },
        {
          path: "introspection",
          name: "introspection",
          component: IntrospectionView,
        },
        {
          path: "introspection/keys",
          component: KeysView,
          children: [
            {
              // hit, missed, mutated
              path: "popular-keys/:keyCat",
              alias: "",
              name: "popularKeys",
              component: PopularKeysView,
            },
          ],
        },
        {
          path: "introspection/prefixes",
          name: "prefixes",
          component: PrefixesView,
        },
        {
          path: "introspection/prefixes/:prefixId",
          component: PrefixView,
          children: [
            {
              path: "",
              name: "prefixOverview",
              component: PrefixOverviewView,
            },
            {
              // hit, missed, mutated
              path: "popular-keys/:keyCat",
              alias: "",
              name: "prefixPopularKeys",
              component: PopularKeysView,
            },
          ],
        },
        {
          path: "introspection/recent-requests",
          name: "recentRequests",
          component: RecentRequestsView,
        },
        {
          path: "alerts",
          name: "alerts",
          component: AlertsView,
        },
        {
          path: "compound-stats",
          name: "compoundStats",
          component: CompoundStatsView,
        },
        {
          path: "slab-stats",
          name: "slabStats",
          component: SlabStatsView,
        },
      ],
    },
    {
      path: "/style-guide",
      name: "styleGuide",
      component: StyleGuideView,
    },
  ],
});

router.beforeEach((to, from, next) => {
  // If route requires auth
  if (to.matched.some(record => record.meta.requiresAuth !== false)) {
    if (!decodeJWT(localStorage.getItem("token"))) {
      // Redirect to the login view if no token found and route requires auth
      next({ name: "login" });
      return;
    }
  }

  next();
});

// Google Analytics page views
router.afterEach(to => {
  let path = to.path;

  // Google Analytics docs on removing inconsistent things, like cache ID:
  // https://developers.google.com/analytics/devguides/collection/analyticsjs/pages#modifying_page_urls
  if (to.params && to.params.cacheId) {
    const cachePath = `caches/${to.params.cacheId}`;
    if (path.indexOf(cachePath) > -1) {
      // e.g. /#/caches/170/alerts => /#/caches/alerts
      path = path.replace(cachePath, "caches");
    }
  }
  if (to.params && to.params.prefixId) {
    const prefixPath = `prefixes/${to.params.prefixId}`;
    if (path.indexOf(prefixPath) > -1) {
      // e.g. /#/caches/introspection/prefixes/3/popular-keys/hit => /#/caches/introspection/prefixes/id/popular-keys/hit
      // replace with `id` to differentiate between /prefixes/ and /prefixes/<id>
      path = path.replace(prefixPath, "prefixes/id");
    }
  }

  // `page` must be set, because Google Analytics ignores the `#/...`
  window.ga("set", "page", path);
  window.ga("send", "pageview");
});

export default router;
